import { errorFrom } from "@/lib/errors";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ChevronDown, Loader2, Plus, SlidersHorizontal } from "lucide-react";
import { Layout } from "@/components/hud/Layout";
import { Button } from "@/components/primitives/Button";
import { FormatPicker } from "@/components/create/FormatPicker";
import { SceneCard } from "@/components/create/SceneCard";
import {
  createScene,
  deleteScene,
  duplicateScene,
  getProject,
  listScenes,
  moveScene,
  updateProject,
  updateScene,
  type AspectRatio,
  type Project,
  type Scene,
} from "@/lib/api";

export default function ScenesBoard() {
  const { t } = useTranslation();
  const { id = "" } = useParams();
  const [project, setProject] = useState<Project | null>(null);
  const [scenes, setScenes] = useState<Scene[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([getProject(id), listScenes(id)]);
      setProject(p);
      setScenes(s);
    } catch {
      toast.error(t("errors.projectNotFound"));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (fn: () => Promise<unknown>, errorMsg: string) => {
    setBusy(true);
    try {
      await fn();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || errorMsg);
    } finally {
      setBusy(false);
    }
  };

  const setAspect = (aspect_ratio: AspectRatio) =>
    run(async () => {
      setProject(await updateProject(id, { aspect_ratio }));
      toast.success(t("scenes.formatSaved", { format: aspect_ratio }));
    }, t("scenes.formatNotSaved"));

  const addScene = () =>
    run(async () => {
      await createScene(id, { title: `Scena ${scenes.length + 1}`, description: "" });
      setScenes(await listScenes(id));
    }, t("scenes.addFailed"));

  const saveScene = (sceneId: string) => async (data: { title?: string; description?: string }) => {
    try {
      const updated = await updateScene(sceneId, data);
      setScenes((prev) => prev.map((s) => (s.id === sceneId ? updated : s)));
    } catch (e: any) {
      toast.error(errorFrom(e, "scenes.editNotSaved"));
    }
  };

  const move = (sceneId: string, dir: "up" | "down") =>
    run(async () => setScenes(await moveScene(sceneId, dir)), t("scenes.reorderFailed"));

  const duplicate = (sceneId: string) =>
    run(async () => {
      await duplicateScene(sceneId);
      setScenes(await listScenes(id));
    }, t("scenes.duplicateFailed"));

  const remove = (sceneId: string) => {
    if (!window.confirm(t("scenes.deleteConfirm"))) return;
    run(async () => {
      await deleteScene(sceneId);
      setScenes(await listScenes(id));
    }, t("scenes.deleteFailed"));
  };

  if (loading) {
    return (
      <Layout>
        <main className="mx-auto max-w-4xl px-6 pt-14 text-sm text-muted-foreground">
          <Loader2 className="inline h-4 w-4 animate-spin" /> Caricamento…
        </main>
      </Layout>
    );
  }
  if (!project) {
    return (
      <Layout>
        <main className="mx-auto max-w-4xl px-6 pt-14">
          <p data-testid="scenes-board-not-found" className="text-sm text-muted-foreground">{t("errors.projectNotFound")}</p>
          <Link to="/dashboard" className="mt-4 inline-block text-sm text-[#2E7BFF]">← {t("scenes.backToProjects")}</Link>
        </main>
      </Layout>
    );
  }

  const aspect: AspectRatio = project.aspect_ratio || "9:16";

  return (
    <Layout>
      <main data-testid="scenes-board-page" className="mx-auto max-w-4xl px-6 pb-24 pt-10">
        <Link to="/dashboard" data-testid="scenes-board-back" className="inline-flex items-center gap-2 font-display text-[11px] font-semibold uppercase tracking-[0.26em] text-muted-foreground transition-colors hover:text-[#7FD4FF]">
          <ArrowLeft className="h-3.5 w-3.5" /> Progetti
        </Link>
        <div className="mt-4 flex flex-wrap items-end justify-between gap-6">
          <div className="min-w-0">
            <span className="eyebrow">Scene Board</span>
            <h1 data-testid="scenes-board-title" className="headline-shadow mt-3 line-clamp-2 font-display text-3xl font-bold uppercase leading-tight tracking-wide text-foreground sm:text-4xl lg:text-5xl">
              {project.title}
            </h1>
            <p className="mt-3 text-sm text-[#B8C4DA]">
              <span data-testid="scenes-count">{scenes.length}</span> {t("scenes.countLabel", { count: scenes.length })} · {t("scenes.formatWord")}{" "}
              <span data-testid="scenes-board-aspect" className="text-[#2E7BFF]">{aspect}</span>. {t("scenes.hint")}
            </p>
          </div>
          <div className="w-full sm:w-80">
            <FormatPicker value={aspect} onChange={setAspect} disabled={busy} compact />
          </div>
        </div>

        {project.story && (
          <div className="mt-8 rounded-2xl glass-panel">
            <button
              type="button"
              data-testid="scenes-board-prompt-toggle"
              onClick={() => setShowPrompt((v) => !v)}
              className="flex w-full items-center justify-between px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground hover:text-foreground"
            >
              {t("scenes.originalPrompt")}
              <ChevronDown className={`h-4 w-4 transition-transform ${showPrompt ? "rotate-180" : ""}`} />
            </button>
            {showPrompt && (
              <p data-testid="scenes-board-prompt" className="whitespace-pre-wrap border-t border-border px-5 py-4 text-sm leading-relaxed text-foreground/80">
                {project.story}
              </p>
            )}
          </div>
        )}

        <section className="mt-8 space-y-4">
          {scenes.length === 0 && (
            <div data-testid="scenes-empty" className="rounded-2xl glass-panel border-dashed p-10 text-center text-sm text-muted-foreground">
              {t("scenes.empty")}
            </div>
          )}
          {scenes.map((s, i) => (
            <SceneCard
              key={s.id}
              scene={s}
              index={i}
              total={scenes.length}
              busy={busy}
              aspect={aspect}
              onImageUploaded={(updated) => setScenes((prev) => prev.map((x) => (x.id === updated.id ? updated : x)))}
              onSave={saveScene(s.id)}
              onMove={(dir) => move(s.id, dir)}
              onDuplicate={() => duplicate(s.id)}
              onDelete={() => remove(s.id)}
            />
          ))}
        </section>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
          <Button variant="secondary" data-testid="scenes-add-btn" onClick={addScene} disabled={busy}>
            <Plus /> {t("scenes.add")}
          </Button>
          <Link
            to={`/projects/${id}`}
            data-testid="scenes-board-advanced-link"
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
          >
            <SlidersHorizontal className="h-3.5 w-3.5" /> {t("scenes.advancedWorkflow")}
          </Link>
        </div>
      </main>
    </Layout>
  );
}
